import { useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { errorMessage } from '@/lib/api'

type JoinCardProps = { onJoin: (name: string, website: string) => Promise<void> }

/** Form di iscrizione ospite: nome (2–30 caratteri) più un honeypot invisibile per gli antispam del backend. */
export function JoinCard({ onJoin }: JoinCardProps) {
  const { t } = useTranslation()
  const [error, setError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const form = new FormData(event.currentTarget)
    const name = String(form.get('name') ?? '').trim()
    const website = String(form.get('website') ?? '')
    setError(null)
    setIsSubmitting(true)
    try {
      await onJoin(name, website)
    } catch (caught) {
      setError(errorMessage(t, caught))
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    // Carta d'imbarco come i biglietti della home: striscia "Iscrizioni aperte", perforazione, nome e un bottone largo.
    <form data-testid="join-form" onSubmit={handleSubmit} className="relative overflow-hidden rounded-xl border border-ink/10 bg-paper shadow-card">
      <p aria-hidden="true" className="bg-sun/25 px-5 pt-3.5 pb-3 font-body text-xs font-semibold tracking-[0.02em] text-sun-ink">
        {t('zambePong.join.ticket')}
      </p>
      <span aria-hidden="true" className="ticket-perforation block" />
      <div className="px-5 pt-4 pb-5">
      <h2 className="font-display font-semibold text-3xl text-ink">{t('zambePong.join.title')}</h2>
      <div className="mt-4">
        <label htmlFor="join-name" className="mb-1.5 block font-body text-sm font-medium text-ink">
          {t('zambePong.join.name')}
        </label>
        <input
          id="join-name"
          name="name"
          type="text"
          required
          minLength={2}
          maxLength={30}
          autoComplete="nickname"
          className="min-h-12 w-full rounded-lg border border-ink-soft/70 bg-paper px-3.5 font-body text-base text-ink focus:border-ink focus:outline-none focus:ring-2 focus:ring-sea/30"
        />
      </div>
      {/* Honeypot: invisibile agli umani, i bot che lo compilano vengono scartati dal backend. */}
      <input
        type="text"
        name="website"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        defaultValue=""
        style={{ position: 'absolute', left: '-9999px' }}
      />
      {error && (
        <p role="alert" className="mt-3 font-body text-sm font-medium text-coral-ink">
          {error}
        </p>
      )}
      <button
        type="submit"
        disabled={isSubmitting}
        className="mt-5 min-h-13 w-full rounded-xl bg-ink px-4 font-body text-base font-semibold text-paper transition-[transform,background-color] duration-150 hover:bg-ink/85 active:scale-[0.99] disabled:opacity-50"
      >
        {isSubmitting ? t('zambePong.join.submitting') : t('zambePong.join.submit')}
      </button>
      </div>
    </form>
  )
}
